import api from './api';

// Types
export interface Kalender {
  kalenderID: number;
  name: string;
  beschreibung?: string;
  typ: string; // Aufgaben, Tickets, Team
  farbe?: string;
  benutzerID?: number;
  benutzerName?: string;
  calDAVSettingID?: number;
  aktiv: boolean;
  reihenfolge: number;
  letzteSync?: string;
  erstelltAm: string;
  mandantID?: number;
  ereignisseCount: number;
}

export interface KalenderEreignis {
  ereignisID: number;
  kalenderID: number;
  kalenderName?: string;
  kalenderFarbe?: string;
  titel: string;
  beschreibung?: string;
  ort?: string;
  startDatum: string;
  endDatum: string;
  ganztaegig: boolean;
  status: string;
  prioritaet?: string;
  farbe?: string;
  ticketID?: number;
  ticketTitel?: string;
  adresseID?: number;
  adresseName?: string;
  calDAVEventUID?: string;
  calDAVLastSync?: string;
  erinnerungMinuten?: number;
  erinnerungGesendet: boolean;
  wiederholungRegel?: string;
  erstelltAm: string;
  erstelltVon?: number;
  erstellerName?: string;
  mandantID?: number;
}

export interface KalenderBerechtigung {
  berechtigungID: number;
  kalenderID: number;
  benutzerID?: number;
  benutzerName?: string;
  gruppeID?: number;
  gruppeName?: string;
  berechtigung: string;
  erstelltAm: string;
}

export interface CreateKalenderRequest {
  name: string;
  beschreibung?: string;
  typ?: string;
  farbe?: string;
  benutzerID?: number;
  calDAVSettingID?: number;
  reihenfolge?: number;
}

export interface UpdateKalenderRequest {
  name?: string;
  beschreibung?: string;
  farbe?: string;
  calDAVSettingID?: number;
  aktiv?: boolean;
  reihenfolge?: number;
}

export interface CreateKalenderEreignisRequest {
  kalenderID: number;
  titel: string;
  beschreibung?: string;
  ort?: string;
  startDatum: string;
  endDatum: string;
  ganztaegig?: boolean;
  prioritaet?: string;
  farbe?: string;
  ticketID?: number;
  adresseID?: number;
  erinnerungMinuten?: number;
  wiederholungRegel?: string;
}

export interface UpdateKalenderEreignisRequest {
  kalenderID?: number;
  titel?: string;
  beschreibung?: string;
  ort?: string;
  startDatum?: string;
  endDatum?: string;
  ganztaegig?: boolean;
  status?: string;
  prioritaet?: string;
  farbe?: string;
  ticketID?: number;
  adresseID?: number;
  erinnerungMinuten?: number;
  wiederholungRegel?: string;
}

export interface KalenderUebersicht {
  kalender: Kalender[];
  ereignisse: KalenderEreignis[];
  vonDatum: string;
  bisDatum: string;
}

export interface KalenderSyncResponse {
  success: boolean;
  message: string;
  ereignisseErstellt: number;
  ereignisseAktualisiert: number;
  ereignisseGeloescht: number;
  fehler: string[];
  syncZeit: string;
}

// Kalender API Service
const kalenderService = {
  // Kalender-Methoden
  getKalender: async (): Promise<Kalender[]> => {
    const response = await api.get<Kalender[]>('/kalender');
    return response.data;
  },

  getKalenderById: async (id: number): Promise<Kalender> => {
    const response = await api.get<Kalender>(`/kalender/${id}`);
    return response.data;
  },

  createKalender: async (data: CreateKalenderRequest): Promise<Kalender> => {
    const response = await api.post<Kalender>('/kalender', data);
    return response.data;
  },

  updateKalender: async (id: number, data: UpdateKalenderRequest): Promise<Kalender> => {
    const response = await api.put<Kalender>(`/kalender/${id}`, data);
    return response.data;
  },

  deleteKalender: async (id: number): Promise<void> => {
    await api.delete(`/kalender/${id}`);
  },

  // Kalender-Übersicht (alle Kalender + Ereignisse für Zeitraum)
  getUebersicht: async (vonDatum?: string, bisDatum?: string): Promise<KalenderUebersicht> => {
    const params: any = {};
    if (vonDatum) params.vonDatum = vonDatum;
    if (bisDatum) params.bisDatum = bisDatum;
    const response = await api.get<KalenderUebersicht>('/kalender/uebersicht', { params });
    return response.data;
  },

  // Ereignis-Methoden
  getEreignisse: async (params?: {
    startDatum?: string;
    endDatum?: string;
    kalenderIds?: string;
    status?: string;
    ticketId?: number;
  }): Promise<KalenderEreignis[]> => {
    const response = await api.get<KalenderEreignis[]>('/kalender/ereignisse', { params });
    return response.data;
  },

  getEreignisById: async (id: number): Promise<KalenderEreignis> => {
    const response = await api.get<KalenderEreignis>(`/kalender/ereignisse/${id}`);
    return response.data;
  },

  createEreignis: async (data: CreateKalenderEreignisRequest): Promise<KalenderEreignis> => {
    const response = await api.post<KalenderEreignis>('/kalender/ereignisse', data);
    return response.data;
  },

  updateEreignis: async (id: number, data: UpdateKalenderEreignisRequest): Promise<KalenderEreignis> => {
    const response = await api.put<KalenderEreignis>(`/kalender/ereignisse/${id}`, data);
    return response.data;
  },

  deleteEreignis: async (id: number): Promise<void> => {
    await api.delete(`/kalender/ereignisse/${id}`);
  },

  createEreignisFromTicket: async (ticketId: number, kalenderId: number): Promise<KalenderEreignis> => {
    const response = await api.post<KalenderEreignis>(
      `/kalender/ereignisse/from-ticket?ticketId=${ticketId}&kalenderId=${kalenderId}`
    );
    return response.data;
  },

  // Berechtigungs-Methoden
  getBerechtigungen: async (kalenderId: number): Promise<KalenderBerechtigung[]> => {
    const response = await api.get<KalenderBerechtigung[]>(`/kalender/${kalenderId}/berechtigungen`);
    return response.data;
  },

  addBerechtigung: async (kalenderId: number, data: {
    benutzerID?: number;
    gruppeID?: number;
    berechtigung: string;
  }): Promise<KalenderBerechtigung> => {
    const response = await api.post<KalenderBerechtigung>(
      `/kalender/${kalenderId}/berechtigungen`,
      data
    );
    return response.data;
  },

  removeBerechtigung: async (berechtigungId: number): Promise<void> => {
    await api.delete(`/kalender/berechtigungen/${berechtigungId}`);
  },

  // CalDAV-Sync
  syncKalender: async (kalenderId: number, forceFullSync?: boolean): Promise<KalenderSyncResponse> => {
    const params = forceFullSync ? { forceFullSync: true } : {};
    const response = await api.post<KalenderSyncResponse>(
      `/kalender/${kalenderId}/sync`,
      null,
      { params }
    );
    return response.data;
  },
};

export default kalenderService;
